import React, {useState, useEffect, useCallback} from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Chip,
    LinearProgress,
    Divider,
    TextField,
    FormControlLabel,
    Switch,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Description as DocumentIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    VideoFile as VideoIcon,
    AudioFile as AudioIcon,
    InsertDriveFile as FileIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    AutoAwesome as AIIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    GridView as MatrixIcon,
} from '@mui/icons-material';

import DocumentTopicMatrix from './DocumentTopicMatrix';

const CourseMaterialsTab = ({courseId, apiClient}) => {
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [materialToDelete, setMaterialToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [updatingVisibility, setUpdatingVisibility] = useState(new Set());

    // Matrix dialog state
    const [matrixDialogOpen, setMatrixDialogOpen] = useState(false);

    // Menu state
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedMaterial, setSelectedMaterial] = useState(null);

    // Edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editIsPublic, setEditIsPublic] = useState(false);
    const [updating, setUpdating] = useState(false);

    // Compact upload form state
    const [uploadFormOpen, setUploadFormOpen] = useState(false);
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadDescription, setUploadDescription] = useState('');
    const [uploadIsPublic, setUploadIsPublic] = useState(false);

    const fetchMaterials = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/api/documents/?course_id=${courseId}`);
            setMaterials(response.documents || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching materials:', err);
            setError('Failed to load course materials');
            setMaterials([]);
        } finally {
            setLoading(false);
        }
    }, [apiClient, courseId]);

    useEffect(() => {
        if (courseId && apiClient) {
            fetchMaterials();
        }
    }, [courseId, apiClient, fetchMaterials]);

    // Poll for embedding status every 15 seconds if any document is not yet embedded
    useEffect(() => {
        const hasPending = materials.some((m) => !m.document_embedded);
        if (!hasPending) return;

        const interval = setInterval(() => {
            fetchMaterials();
        }, 15000);

        return () => clearInterval(interval);
    }, [materials, fetchMaterials]);

    const getFileIcon = (fileType) => {
        if (fileType?.includes('pdf')) return <PdfIcon/>;
        if (fileType?.includes('image')) return <ImageIcon/>;
        if (fileType?.includes('video')) return <VideoIcon/>;
        if (fileType?.includes('audio')) return <AudioIcon/>;
        if (fileType?.includes('text') || fileType?.includes('document')) return <DocumentIcon/>;
        return <FileIcon/>;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleDeleteMaterial = async () => {
        if (!materialToDelete) return;

        setDeleting(true);
        try {
            await apiClient.delete(`/api/documents/${materialToDelete.id}`);
            setMaterials(materials.filter((m) => m.id !== materialToDelete.id));
            setDeleteDialogOpen(false);
            setMaterialToDelete(null);
        } catch (err) {
            console.error('Error deleting material:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to delete material');
        } finally {
            setDeleting(false);
        }
    };

    const handleDownload = async (material) => {
        try {
            const response = await apiClient.get(`/api/documents/${material.id}/download`, {}, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', material.original_filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading file:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to download file');
        }
    };

    const handleToggleVisibility = async (material) => {
        const materialId = material.id;
        setUpdatingVisibility((prev) => new Set(prev).add(materialId));

        try {
            await apiClient.put(`/api/documents/${materialId}`, {
                is_public: !material.is_public,
            });
            setMaterials((prevMaterials) =>
                prevMaterials.map((m) =>
                    m.id === materialId ? {...m, is_public: !m.is_public} : m
                )
            );
        } catch (err) {
            console.error('Error updating document visibility:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to update document visibility');
        } finally {
            setUpdatingVisibility((prev) => {
                const newSet = new Set(prev);
                newSet.delete(materialId);
                return newSet;
            });
        }
    };

    const handleMenuClick = (event, material) => {
        setMenuAnchor(event.currentTarget);
        setSelectedMaterial(material);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedMaterial(null);
    };

    const handleEdit = () => {
        if (selectedMaterial) {
            setEditTitle(selectedMaterial.title || '');
            setEditDescription(selectedMaterial.description || '');
            setEditIsPublic(selectedMaterial.is_public);
            setEditDialogOpen(true);
        }
        handleMenuClose();
    };

    const handleEditSave = async () => {
        if (!selectedMaterial) return;

        setUpdating(true);
        try {
            await apiClient.put(`/api/documents/${selectedMaterial.id}`, {
                title: editTitle.trim() || null,
                description: editDescription.trim() || null,
                is_public: editIsPublic,
            });

            setMaterials((prevMaterials) =>
                prevMaterials.map((m) =>
                    m.id === selectedMaterial.id
                        ? {
                            ...m,
                            title: editTitle.trim() || null,
                            description: editDescription.trim() || null,
                            is_public: editIsPublic,
                            display_name: editTitle.trim() || m.original_filename,
                        }
                        : m
                )
            );

            setEditDialogOpen(false);
        } catch (err) {
            console.error('Error updating document:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to update document');
        } finally {
            setUpdating(false);
        }
    };

    const handleQuickUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploading(true);
        setUploadProgress(0);
        setError(null);

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const formData = new FormData();
                formData.append('file', file);
                formData.append('course_id', courseId);
                formData.append('is_public', false);
                // REMOVED: formData.append('topic_id', topic_id);
                // NOW: topic_ids defaults to empty array (available to all topics)

                await apiClient.post('/api/documents/upload', formData);
                setUploadProgress(((i + 1) / files.length) * 100);
            }

            await fetchMaterials();
        } catch (err) {
            console.error('Error uploading files:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to upload files');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            event.target.value = '';
        }
    };

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploading(true);
        setUploadProgress(0);
        setError(null);

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const formData = new FormData();
                formData.append('file', file);
                formData.append('course_id', courseId);

                if (uploadTitle.trim()) {
                    formData.append('title', uploadTitle.trim());
                }
                if (uploadDescription.trim()) {
                    formData.append('description', uploadDescription.trim());
                }
                formData.append('is_public', uploadIsPublic);

                await apiClient.post('/api/documents/upload', formData);
                setUploadProgress(((i + 1) / files.length) * 100);
            }

            await fetchMaterials();

            setUploadTitle('');
            setUploadDescription('');
            setUploadIsPublic(false);
            setUploadFormOpen(false);
        } catch (err) {
            console.error('Error uploading files:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to upload files');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            event.target.value = '';
        }
    };

    const openDeleteDialog = (material) => {
        setMaterialToDelete(material);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        if (!deleting) {
            setDeleteDialogOpen(false);
            setMaterialToDelete(null);
        }
    };

    const handleMatrixSave = () => {
        // Refresh materials after matrix changes
        fetchMaterials();
        setMatrixDialogOpen(false);
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header with Upload */}
            <Box sx={{mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <Box>
                    <Typography variant="h6" gutterBottom sx={{display: 'flex', alignItems: 'center'}}>
                        <CloudUploadIcon sx={{mr: 1}}/>
                        Course Materials ({materials.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Upload and manage files for your course
                    </Typography>
                </Box>

                <Box sx={{display: 'flex', gap: 1}}>
                    {/* Matrix Button */}
                    <Tooltip title="Manage Topic Associations">
                        <Button
                            variant="outlined"
                            startIcon={<MatrixIcon/>}
                            onClick={() => setMatrixDialogOpen(true)}
                            disabled={uploading}
                        >
                            Topics
                        </Button>
                    </Tooltip>

                    {/* Quick Upload Button */}
                    <input
                        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.mp4,.mov,.ppt,.pptx,.xls,.xlsx"
                        style={{display: 'none'}}
                        id="quick-upload"
                        multiple
                        type="file"
                        onChange={handleQuickUpload}
                        disabled={uploading}
                    />
                    <label htmlFor="quick-upload">
                        <Button
                            variant="contained"
                            component="span"
                            startIcon={<CloudUploadIcon/>}
                            disabled={uploading}
                        >
                            {uploading ? 'Uploading...' : 'Upload Files'}
                        </Button>
                    </label>

                    {/* Advanced Upload Button */}
                    <Button variant="outlined" onClick={() => setUploadFormOpen(true)} disabled={uploading}>
                        Advanced
                    </Button>
                </Box>
            </Box>

            {uploading && (
                <Box sx={{mb: 3}}>
                    <LinearProgress variant="determinate" value={uploadProgress}/>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 1, textAlign: 'center'}}>
                        Uploading... {Math.round(uploadProgress)}%
                    </Typography>
                </Box>
            )}

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{mb: 3}} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Materials List */}
            <Paper sx={{p: 0}}>
                <Box sx={{p: 2, borderBottom: 1, borderColor: 'divider'}}>
                    <Typography variant="h6">Materials</Typography>
                </Box>

                {materials.length === 0 ? (
                    <Box sx={{p: 4, textAlign: 'center'}}>
                        <CloudUploadIcon sx={{fontSize: 48, color: 'text.secondary', mb: 2}}/>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            No materials uploaded yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Upload files to get started with your course content
                        </Typography>
                    </Box>
                ) : (
                    <List>
                        {materials.map((material, index) => (
                            <React.Fragment key={material.id}>
                                <ListItem>
                                    <ListItemIcon>{getFileIcon(material.file_type)}</ListItemIcon>
                                    <ListItemText
                                        primary={material.display_name}
                                        secondary={
                                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5}}>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatFileSize(material.file_size)} •{' '}
                                                        {material.file_type.split('/')[1]?.toUpperCase()}
                                                    </Typography>
                                                    {material.is_public ? (
                                                        <Chip
                                                            label="Visible to Students"
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                            icon={<VisibilityIcon/>}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label="Private"
                                                            size="small"
                                                            color="default"
                                                            variant="outlined"
                                                            icon={<VisibilityOffIcon/>}
                                                        />
                                                    )}
                                                    {/* New status chip */}
                                                    {!material.document_embedded ? (
                                                        <Chip
                                                            icon={<CircularProgress size={12}/>}
                                                            label="Processing"
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ml: 0.5}}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<AIIcon/>}
                                                            label="Available"
                                                            size="small"
                                                            variant="outlined"
                                                            color="success"
                                                            sx={{ml: 0.5}}
                                                        />
                                                    )}
                                                </Box>
                                                {material.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {material.description}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color="text.secondary">
                                                    Uploaded: {new Date(material.created_at).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Tooltip title="More actions">
                                            <IconButton
                                                edge="end"
                                                aria-label="more actions"
                                                onClick={(e) => handleMenuClick(e, material)}
                                            >
                                                <MoreVertIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                {index < materials.length - 1 && <Divider/>}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Paper>

            {/* Action Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                transformOrigin={{horizontal: 'right', vertical: 'top'}}
                anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
            >
                <MenuItem
                    onClick={() => {
                        handleDownload(selectedMaterial);
                        handleMenuClose();
                    }}
                >
                    <DownloadIcon sx={{mr: 1}}/>
                    Download
                </MenuItem>
                <MenuItem onClick={handleEdit}>
                    <EditIcon sx={{mr: 1}}/>
                    Edit
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleToggleVisibility(selectedMaterial);
                        handleMenuClose();
                    }}
                >
                    {selectedMaterial?.is_public ? (
                        <VisibilityOffIcon sx={{mr: 1}}/>
                    ) : (
                        <VisibilityIcon sx={{mr: 1}}/>
                    )}
                    {selectedMaterial?.is_public ? 'Make Private' : 'Make Visible to Students'}
                </MenuItem>
                <Divider/>
                <MenuItem
                    onClick={() => {
                        openDeleteDialog(selectedMaterial);
                        handleMenuClose();
                    }}
                    sx={{color: 'error.main'}}
                >
                    <DeleteIcon sx={{mr: 1}}/>
                    Delete
                </MenuItem>
            </Menu>

            {/* Document-Topic Matrix Dialog */}
            <DocumentTopicMatrix
                open={matrixDialogOpen}
                onClose={() => setMatrixDialogOpen(false)}
                onSave={handleMatrixSave}
                courseId={courseId}
                apiClient={apiClient}
            />

            {/* Advanced Upload Dialog */}
            <Dialog open={uploadFormOpen} onClose={() => setUploadFormOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Advanced Upload</DialogTitle>
                <DialogContent>
                    <Box sx={{mt: 1}}>
                        <TextField
                            fullWidth
                            label="Title (optional)"
                            value={uploadTitle}
                            onChange={(e) => setUploadTitle(e.target.value)}
                            placeholder="Custom title for the uploaded file"
                            sx={{mb: 2}}
                        />
                        <TextField
                            fullWidth
                            label="Description (optional)"
                            value={uploadDescription}
                            onChange={(e) => setUploadDescription(e.target.value)}
                            placeholder="Description of the file content"
                            multiline
                            rows={3}
                            sx={{mb: 2}}
                        />
                        <FormControlLabel
                            control={
                                <Switch checked={uploadIsPublic} onChange={(e) => setUploadIsPublic(e.target.checked)}/>
                            }
                            label="Make visible to students"
                        />

                        <Box sx={{mt: 3, textAlign: 'center'}}>
                            <input
                                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.mp4,.mov,.ppt,.pptx,.xls,.xlsx"
                                style={{display: 'none'}}
                                id="advanced-upload"
                                multiple
                                type="file"
                                onChange={handleFileUpload}
                                disabled={uploading}
                            />
                            <label htmlFor="advanced-upload">
                                <Button variant="contained" component="span" startIcon={<CloudUploadIcon/>}
                                        disabled={uploading} size="large" fullWidth>
                                    {uploading ? 'Uploading...' : 'Select Files'}
                                </Button>
                            </label>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadFormOpen(false)} disabled={uploading}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Document Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Document</DialogTitle>
                <DialogContent>
                    <Box sx={{mt: 1}}>
                        <TextField
                            fullWidth
                            label="Title"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            placeholder="Document title"
                            sx={{mb: 2}}
                            disabled={updating}
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            placeholder="Document description"
                            multiline
                            rows={3}
                            sx={{mb: 2}}
                            disabled={updating}
                        />
                        <FormControlLabel
                            control={
                                <Switch checked={editIsPublic} onChange={(e) => setEditIsPublic(e.target.checked)}
                                        disabled={updating}/>
                            }
                            label="Make visible to students"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={updating}>
                        Cancel
                    </Button>
                    <Button onClick={handleEditSave} variant="contained" disabled={updating}
                            startIcon={updating ? <CircularProgress size={20}/> : null}>
                        {updating ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Delete Material</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to delete "{materialToDelete?.display_name}"?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteMaterial} color="error" variant="contained" disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={20}/> : <DeleteIcon/>}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CourseMaterialsTab;